import React, { FC, Fragment, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { permissions } from 'utils/permission';
import { useTranslation } from 'react-i18next';
import { Tab, Box, Badge, Backdrop, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import DentistOrders from 'pages/order-list/orders/dentist-orders.component';
import LabOrders from 'pages/order-list/orders/lab-orders.component';
import DraftOrders from 'pages/order-list/draft-orders/draft-orders.component';
import OrderStatusViewTab from 'pages/order-list/order-status-view/order-status-view-tab.component';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { useNavigate, useParams } from 'react-router-dom';
import CertifyOrders from 'pages/order-list/certify-orders/certify-orders.component';
import DayOverview from 'components/delivery-schedule/day-overview.component';
import Milling from './milling/milling.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useMutation } from '@apollo/client';
import { batchArchiveOrdersTemplate } from 'apollo/templates/order';
import { Order } from 'models/order';
import BatchArchiveConfirmDialog from 'components/shared/batch-archive-confirm-dialog/batch-archive-confirm-dialog.component';
import BatchActionBar, { BATCH_SELECTION_LIMIT } from 'components/orders/shared/batch-action-bar.component';
import {
    downloadBatchOrdersPdf,
    getBatchPdfActions,
    isBatchPdfActionDisabled,
    isBatchPdfActionType,
} from 'components/orders/shared/batch-pdf-actions';
import {
    getBatchOrderActions,
    isBatchOrderActionDisabled,
    isBatchOrderActionType,
} from 'components/orders/shared/batch-order-actions';
import { useBatchOrderActions } from 'components/orders/shared/use-batch-order-actions';
import RecentDeliveryNoteBatches from 'components/orders/shared/recent-batches/recent-delivery-note-batches.component';
import OrderForms from './order-forms/order-forms.component';
import ScannerCases from './scanner-cases/scanner-cases.component';
import DentistScannerCases from './dentist-scanner-cases/dentist-scanner-cases.component';
import { useActiveOrderListProfile } from 'components/orders/lab-order-table/use-active-order-list-profile';
import ListViewProfileSelector from 'components/orders/lab-order-table/list-view-profile-selector.component';
import { SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE } from 'components/orders/lab-order-table/column-registry';

interface CheckedOrder extends Order {
    checked: boolean;
    orderIndex: number;
}

const OrderList: FC = () => {
    const { t: translate } = useTranslation(['orders', 'scan-cases', 'milling-jobs', 'order-forms', 'organization']);
    const { tab } = useParams<{ tab: string }>();
    const [tabState, setTabState] = useState(tab || 'list');
    const [unCertifiedOrderCount, setUnCertifiedOrderCount] = useState(0);
    const [batchActionSelected, setBatchActionSelected] = React.useState(false);
    const [checkedOrders, setCheckedOrders] = useState<Map<string, CheckedOrder>>(new Map());
    const checkedOrderIds = useMemo(() => new Set(checkedOrders.keys()), [checkedOrders]);
    const [selectedOrders, setSelectedOrders] = useState<Order[]>();
    const [error, setError] = useState<Error | null>(null);
    const [spinner, setSpinner] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [refetchOrder, SetRefetchOrder] = useState<boolean>(false);
    const currentUser = currentUserVar();
    const navigate = useNavigate();
    const currentOrganization = currentOrgVar();
    const isCertificationEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );
    const isScannerCasesEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.SCAN_CASES,
    );
    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );

    const isOrderFormEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.ORDER_FORM_AI,
    );

    const isDentist = permissions.isDentist(currentUser.type);
    const isLabTechnician = permissions.isLabTechnician(currentUser.type);
    const isExternalTechnician = permissions.isExternalTechnician(currentUser.role);
    const isElevatedExternalUser = permissions.isElevatedExternalUser(currentUser.role);
    const showListViewProfileSelector = isLabTechnician && !isExternalTechnician;
    const {
        profiles: listViewProfiles,
        activeProfile: activeListViewProfile,
        activeProfileId: activeListViewProfileId,
        selectProfile: selectListViewProfile,
        loading: listViewProfileLoading,
    } = useActiveOrderListProfile({
        enabled: showListViewProfileSelector,
        systemDefault: SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE,
        scope: 'MAIN',
    });
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );
    const actions = [
        ...getBatchPdfActions(translate),
        ...getBatchOrderActions(translate),
        { icon: <ArchiveIcon />, name: translate('orders:ARCHIVE_ORDER'), type: 'ARCHIVE_ORDERS' },
    ];
    // DD-3463: production finished / send to clinic dialogs and their compiled-PDF snackbar.
    const { openBatchOrderAction, dialogs: batchOrderActionDialogs } = useBatchOrderActions({
        onCompleted: () => {
            setBatchActionSelected(false);
            setCheckedOrders(new Map());
            SetRefetchOrder(true);
        },
    });

    // TODO: Move to shared
    const isActionBasedOrder = () => {
        return (
            CONSTANTS.SETTINGS_VALUES.ACTION_BASED ===
            customerSettings.getCustomerSetting(
                currentOrganization.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ORDER_VERSION,
            )
        );
    };
    const [batchArchiveOrders] = useMutation(batchArchiveOrdersTemplate, {
        onCompleted: () => {
            setSpinner(false);
            setBatchActionSelected(false);
            setCheckedOrders(new Map());
            SetRefetchOrder(true);
        },
        onError(error) {
            setError(error);
            setSpinner(false);
        },
    });
    const handleTabChange = (_: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`/orders/${newTabState}`);
            setTabState(newTabState);
            setBatchActionSelected(false);
            setCheckedOrders(new Map());
        }
    };

    useEffect(() => {
        handleTabChange(undefined, tab || 'list');
    }, [tab]);

    const handleCertifyUnCertify = (isCertified: boolean) => {
        setUnCertifiedOrderCount(unCertifiedOrderCount + (isCertified ? -1 : 1));
    };

    const selectedCount = checkedOrders.size;
    const isAtLimit = selectedCount >= BATCH_SELECTION_LIMIT;

    const handleCheckedOrders = useCallback((order: Order, checked: boolean, orderIndex: number) => {
        setCheckedOrders((current) => {
            if (checked && current.size >= BATCH_SELECTION_LIMIT) {
                return current;
            }
            const updated = new Map(current);
            if (checked) {
                updated.set(order._id as string, { ...order, checked, orderIndex });
            } else {
                updated.delete(order._id as string);
            }
            return updated;
        });
    }, []);

    const isAnyOrdersChecked = () => {
        return !!Array.from(checkedOrders.values()).find((value) => value.checked);
    };

    const handleStartBatchActionOrderClick = async (type: string) => {
        const orders = Array.from(checkedOrders.values()).sort((a, b) => a.orderIndex - b.orderIndex);
        setSelectedOrders(orders as Order[]);
        const orderIds = orders.map((order) => order._id);
        setError(null);
        SetRefetchOrder(false);
        if (type === 'ARCHIVE_ORDERS') {
            setConfirmDialogOpen(true);
            return;
        }
        if (openBatchOrderAction(type, orderIds as string[])) {
            return;
        }
        if (isBatchPdfActionType(type)) {
            await handleBatchOrdersPdfPrint(orderIds as string[], type);
        }
    };

    const handleBatchOrdersPdfPrint = async (orderIds: string[], type: string) => {
        try {
            setSpinner(true);
            await downloadBatchOrdersPdf(orderIds, type, translate);
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner(false);
            setBatchActionSelected(false);
            setCheckedOrders(new Map());
        }
    };

    const disableOrdersBatch = (type: string) => {
        const hasMissingDeliveryNote = Array.from(checkedOrders.values()).some(
            (order) => order.checked && !order.hasDeliveryNotePdf,
        );

        if (type === 'ARCHIVE_ORDERS') {
            return isExternalTechnician;
        }
        if (isBatchOrderActionType(type)) {
            return isBatchOrderActionDisabled(type, {
                isDentist,
                isExternalTechnician,
                isElevatedExternalUser,
            });
        }
        return isBatchPdfActionDisabled(type, {
            isDentist,
            isExternalTechnician,
            isElevatedExternalUser,
            isExternalLabUsersEnabled,
            hasMissingDeliveryNote,
        });
    };

    const confirmArchiveOrders = async (filteredOrders: Order[]) => {
        setConfirmDialogOpen(false);
        setSpinner(true);
        const orderIds = filteredOrders.map((order) => order._id);
        await batchArchiveOrders({
            variables: {
                orderIds: orderIds,
            },
        });
    };

    return (
        <Fragment>
            {spinner ? (
                <Backdrop open={spinner} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <CircularProgress sx={{ zIndex: 10000, position: 'absolute', top: '50%', left: '50%' }} size={80} />
                </Backdrop>
            ) : undefined}
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            <TabContext value={tabState}>
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                        <Tab label={translate('orders:ORDERS')} value="list" />
                        {isActionBasedOrder() && isDentist ? (
                            <Tab label={translate('orders:DRAFT_ORDERS')} value="drafts" />
                        ) : undefined}
                        {!isExternalTechnician ? (
                            <Tab label={translate('orders:STATUS_VIEW')} value="status-view" />
                        ) : undefined}
                        {isDentist && isCertificationEnabled ? (
                            <Tab
                                label={<Badge color="error">{translate('orders:CERTIFY')}&nbsp;</Badge>}
                                value="certify"
                            />
                        ) : undefined}
                        {isLabTechnician && !isExternalTechnician ? (
                            <Tab label={translate('orders:DELIVERY_SCHEDULE')} value="internal-delivery-schedule" />
                        ) : undefined}
                        {isLabTechnician && isExternalLabUsersEnabled ? (
                            <Tab
                                label={translate('orders:EXTERNAL_DELIVERY_SCHEDULE')}
                                value="external-delivery-schedule"
                            />
                        ) : undefined}
                        {isLabTechnician && !isExternalTechnician && isScannerCasesEnabled ? (
                            <Tab label={translate('scan-cases:SCAN_CASES')} value="scanner-cases" />
                        ) : undefined}
                        {isDentist && isScannerCasesEnabled ? (
                            <Tab label={translate('scan-cases:SCAN_CASES')} value="dentist-scanner-cases" />
                        ) : undefined}
                        {isLabTechnician && isOrderFormEnabled ? (
                            <Tab label={translate('order-forms:ORDER_FORMS')} value="order-forms" />
                        ) : undefined}
                        {isLabTechnician && isLabMillingEnabled ? (
                            <Tab label={translate('milling-jobs:MILLING_JOBS')} value="milling" />
                        ) : undefined}
                    </TabList>
                    {tabState === 'list' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FormControlLabel
                                checked={batchActionSelected}
                                onChange={() => {
                                    setBatchActionSelected(!batchActionSelected);
                                    setCheckedOrders(new Map());
                                }}
                                control={<Checkbox />}
                                label={translate('orders:BATCH_ACTION')}
                            />
                            <RecentDeliveryNoteBatches />
                            {showListViewProfileSelector ? (
                                <ListViewProfileSelector
                                    profiles={listViewProfiles}
                                    activeProfileId={activeListViewProfileId}
                                    onChange={selectListViewProfile}
                                />
                            ) : undefined}
                        </Box>
                    ) : undefined}
                </Box>

                <TabPanel sx={{ padding: 0 }} value="list">
                    <Suspense fallback={null}>
                        {isDentist ? (
                            <DentistOrders
                                batchActionSelected={batchActionSelected}
                                onHandleCheckedOrders={handleCheckedOrders}
                                onFilterChange={() => setCheckedOrders(new Map())}
                                refetchOrder={refetchOrder}
                                isAtLimit={isAtLimit}
                                checkedOrderIds={checkedOrderIds}
                            />
                        ) : (
                            <LabOrders
                                batchActionSelected={batchActionSelected}
                                onHandleCheckedOrders={handleCheckedOrders}
                                onSortChange={() => setCheckedOrders(new Map())}
                                onFilterChange={() => setCheckedOrders(new Map())}
                                refetchOrder={refetchOrder}
                                labOrderListProfile={activeListViewProfile}
                                profileLoading={listViewProfileLoading}
                                isAtLimit={isAtLimit}
                                checkedOrderIds={checkedOrderIds}
                            />
                        )}
                        {batchActionSelected && isAnyOrdersChecked() ? (
                            <BatchActionBar
                                selectedCount={selectedCount}
                                limit={BATCH_SELECTION_LIMIT}
                                actions={actions.map((action) => ({
                                    ...action,
                                    disabled: disableOrdersBatch(action.type),
                                }))}
                                onActionClick={handleStartBatchActionOrderClick}
                                onClearAll={() => setCheckedOrders(new Map())}
                            />
                        ) : undefined}
                    </Suspense>
                </TabPanel>
                <TabPanel sx={{ padding: 0 }} value="drafts">
                    <Suspense fallback={null}>
                        <DraftOrders />
                    </Suspense>
                </TabPanel>
                <TabPanel sx={{ padding: 0 }} value="status-view">
                    <Suspense fallback={null}>
                        <OrderStatusViewTab />
                    </Suspense>
                </TabPanel>
                {isDentist && isCertificationEnabled ? (
                    <TabPanel sx={{ padding: 0 }} value="certify">
                        <Suspense fallback={null}>
                            <CertifyOrders isDentist={isDentist} handleCertifyUnCertify={handleCertifyUnCertify} />
                        </Suspense>
                    </TabPanel>
                ) : undefined}
                {isLabTechnician && !isExternalTechnician ? (
                    <TabPanel sx={{ padding: 0 }} value="internal-delivery-schedule">
                        <Suspense fallback={null}>
                            <DayOverview isExternal={false} />
                        </Suspense>
                    </TabPanel>
                ) : undefined}
                {isLabTechnician && isExternalLabUsersEnabled ? (
                    <TabPanel sx={{ padding: 0 }} value="external-delivery-schedule">
                        <Suspense fallback={null}>
                            <DayOverview isExternal={true} />
                        </Suspense>
                    </TabPanel>
                ) : undefined}
                {isLabTechnician && !isExternalTechnician && isScannerCasesEnabled ? (
                    <TabPanel sx={{ padding: 0 }} value="scanner-cases">
                        <Suspense fallback={null}>
                            <ScannerCases />
                        </Suspense>
                    </TabPanel>
                ) : undefined}
                {isDentist && isScannerCasesEnabled ? (
                    <TabPanel sx={{ padding: 0 }} value="dentist-scanner-cases">
                        <Suspense fallback={null}>
                            <DentistScannerCases />
                        </Suspense>
                    </TabPanel>
                ) : undefined}
                {isLabTechnician && isOrderFormEnabled ? (
                    <TabPanel sx={{ padding: 0 }} value="order-forms">
                        <Suspense fallback={null}>
                            <OrderForms />
                        </Suspense>
                    </TabPanel>
                ) : undefined}
                {isLabTechnician && isLabMillingEnabled ? (
                    <TabPanel sx={{ padding: 0 }} value="milling">
                        <Suspense fallback={null}>
                            <Milling />
                        </Suspense>
                    </TabPanel>
                ) : undefined}
            </TabContext>
            <BatchArchiveConfirmDialog
                open={confirmDialogOpen}
                selectedOrders={selectedOrders}
                handleConfirm={confirmArchiveOrders}
                handleClose={() => setConfirmDialogOpen(false)}
            />
            {batchOrderActionDialogs}
        </Fragment>
    );
};

export default OrderList;
